import {
  Activity,
  BadgePlus,
  Calculator,
  Gauge,
  Briefcase,
  CheckSquare,
  LifeBuoy,
  Link2,
  type LucideIcon,
  Mail,
  Repeat,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Table2,
  Wallet,
} from "lucide-react";
import { Navigate } from "react-router-dom";
import { lazy } from "react";
import type { ReactElement, ReactNode } from "react";

// Eager imports: small shared wrappers + the tab-route registries. The tab
// *components* inside each registry are already lazy (see the `*-tabs.tsx`
// files), and `EnergyAdvisorRouteWrapper` is a thin context wrapper shared across
// every Energy Advisor route — lazy-splitting it would only add a redundant
// Suspense waterfall in front of the already-lazy page it wraps.
import { EnergyAdvisorRouteWrapper } from "@/pages/energy-advisor/EnergyAdvisorRouteWrapper";
import { MAXWELL_TAB_ROUTES } from "@/pages/maxwell/maxwell-tabs";
import { ENERGY_ADVISOR_CUSTOMER_TAB_ROUTES } from "@/pages/energy-advisor/customer-tabs";
import { DR_PEAK_SHAVING_TAB_ROUTES } from "@/pages/dr-peak-shaving/dr-tabs";
import { SES_CALCULATOR_TAB_ROUTES } from "@/pages/ses-calculators/ses-calculator-tabs";

// Lazy page chunks. Each top-level page is code-split into its own chunk so
// the initial bundle no longer carries every page's code — React Router
// downloads a page's chunk on first navigation, with the router-level
// `<Suspense>` rendering `<RouteFallback>` during the gap. Pages export their
// component as a named export, so each loader maps that name onto `default`
// (what `React.lazy` expects).
const AccessDeniedPage = lazy(() =>
  import("@/pages/access-denied/AccessDeniedPage").then((m) => ({
    default: m.AccessDeniedPage,
  })),
);
const TasksPage = lazy(() =>
  import("@/pages/tasks/TasksPage").then((m) => ({ default: m.TasksPage })),
);
const CasesPage = lazy(() =>
  import("@/pages/cases/CasesPage").then((m) => ({ default: m.CasesPage })),
);
const LoginPage = lazy(() =>
  import("@/pages/auth/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const PortalRfqPage = lazy(() =>
  import("@/pages/portal/PortalRfqPage").then((m) => ({
    default: m.PortalRfqPage,
  })),
);
const AgentDealsPage = lazy(() =>
  import("@/pages/portal/AgentDealsPage").then((m) => ({
    default: m.AgentDealsPage,
  })),
);
const TenantSelectPage = lazy(() =>
  import("@/pages/auth/TenantSelectPage").then((m) => ({
    default: m.TenantSelectPage,
  })),
);
const DesignPage = lazy(() =>
  import("@/pages/design/DesignPage").then((m) => ({ default: m.DesignPage })),
);
const SupplierRfqsPage = lazy(() =>
  import("@/pages/supplier-rfqs/SupplierRfqsPage").then((m) => ({
    default: m.SupplierRfqsPage,
  })),
);
const SupplierRfqDetail = lazy(() =>
  import("@/pages/supplier-rfqs/SupplierRfqDetail").then((m) => ({
    default: m.SupplierRfqDetail,
  })),
);
const LauncherPage = lazy(() =>
  import("@/pages/launcher/LauncherPage").then((m) => ({
    default: m.LauncherPage,
  })),
);
const MaxwellWorkspacePage = lazy(() =>
  import("@/pages/maxwell/MaxwellWorkspacePage").then((m) => ({
    default: m.MaxwellWorkspacePage,
  })),
);
const MatrixPricingPage = lazy(() =>
  import("@/pages/matrix/MatrixPricingPage").then((m) => ({
    default: m.MatrixPricingPage,
  })),
);
const DrPeakShavingPage = lazy(() =>
  import("@/pages/dr-peak-shaving/DrPeakShavingPage").then((m) => ({
    default: m.DrPeakShavingPage,
  })),
);
const SesCalculatorsPage = lazy(() =>
  import("@/pages/ses-calculators/SesCalculatorsPage").then((m) => ({
    default: m.SesCalculatorsPage,
  })),
);
const CaptureHealthPage = lazy(() =>
  import("@/pages/matrix/CaptureHealthPage").then((m) => ({
    default: m.CaptureHealthPage,
  })),
);
const SupplierCapturePage = lazy(() =>
  import("@/pages/matrix/SupplierCapturePage").then((m) => ({
    default: m.SupplierCapturePage,
  })),
);
const MatrixMappingPage = lazy(() =>
  import("@/pages/matrix/mapping/MappingPage").then((m) => ({
    default: m.MappingPage,
  })),
);
const EnergyAdvisorCustomerPage = lazy(() =>
  import("@/pages/energy-advisor/EnergyAdvisorCustomerPage").then((m) => ({
    default: m.EnergyAdvisorCustomerPage,
  })),
);
const EnergyAdvisorEntryRoute = lazy(() =>
  import("@/pages/energy-advisor/EnergyAdvisorEntryRoute").then((m) => ({
    default: m.EnergyAdvisorEntryRoute,
  })),
);
const EnergyAdvisorRfqWalkthroughPage = lazy(() =>
  import("@/pages/energy-advisor/rfq/EnergyAdvisorRfqWalkthroughPage").then(
    (m) => ({
      default: m.EnergyAdvisorRfqWalkthroughPage,
    }),
  ),
);
const PayeesPage = lazy(() =>
  import("@/pages/payees/PayeesPage").then((m) => ({ default: m.PayeesPage })),
);
const SupplierRepliesPage = lazy(() =>
  import("@/pages/supplier-replies/SupplierRepliesPage").then((m) => ({
    default: m.SupplierRepliesPage,
  })),
);
const PortalLinksPage = lazy(() =>
  import("@/pages/settings/PortalLinksPage").then((m) => ({
    default: m.PortalLinksPage,
  })),
);
const AutoRenewalParametersPage = lazy(() =>
  import("@/pages/settings/AutoRenewalParametersPage").then((m) => ({
    default: m.AutoRenewalParametersPage,
  })),
);
const MatrixAdderDefaultsPage = lazy(() =>
  import("@/pages/settings/MatrixAdderDefaultsPage").then((m) => ({
    default: m.MatrixAdderDefaultsPage,
  })),
);
const PassthroughRatesPage = lazy(() =>
  import("@/pages/settings/PassthroughRatesPage").then((m) => ({
    default: m.PassthroughRatesPage,
  })),
);
const ScoringModelPage = lazy(() =>
  import("@/pages/settings/ScoringModelPage").then((m) => ({
    default: m.ScoringModelPage,
  })),
);
import type { FeatureName } from "@/lib/features";
import type { Breadcrumb, PageMeta } from "@/lib/page-meta";
import type { SessionFeatureFlags } from "@/types/generated/SessionFeatureFlags";

/**
 * Sales's own section keys. Kept as a named union because `APP_ROUTES` below
 * still uses them; every other app declares its section keys inline in its
 * definition (`lib/modules/*`), and `AppRoute.section` is a plain `string` so no
 * per-module union has to be threaded through this module any more.
 *
 * `GlobalNavSection` and `ReportsNavSection` used to live here for exactly
 * that threading. Both are gone with their registries.
 */
export type NavSection =
  | "workspace"
  | "ops-workspace"
  | "energy-advisor-settings"
  | "operations-settings";

/**
 * A child tab inside a detail page. Declared on the parent route's `children`
 * array; the router nests these under the parent path and the parent
 * component renders `<RouterTabs />` + `<Outlet />` to display them.
 *
 * See "Tabbed detail pages" in CLAUDE.md for the full pattern.
 */
export interface TabRoute {
  /** Path segment relative to the parent (e.g. `"bills"` under `/clients/:id`
   *  produces `/clients/:id/bills`). No leading slash. */
  path: string;
  /** Display label on the tab trigger. */
  label: string;
  /** Optional icon on the tab trigger. */
  icon?: LucideIcon;
  /** Optional trailing adornment on the trigger (e.g. an unread-count badge).
   *  Rendered after the label on the desktop strip; omit for plain tabs. */
  badge?: ReactNode;
  /** React element rendered when this tab is active. */
  element: ReactElement;
  /** Optional — when set, the parent's `<RouterTabs />` renders the trigger
   *  disabled and the route isn't registered. Use for tabs that aren't ported
   *  yet but should still appear in the bar as placeholders. */
  disabled?: boolean;
  /** Optional — when set, the parent detail page must filter the tab out
   *  of `<RouterTabs>` when the feature flag is off. The router still
   *  materializes the route (so URLs resolve), and server-side checks
   *  remain the source of truth — this is UX, matching how
   *  `requiredPermission` works on top-level `AppRoute`s. */
  requiredFeature?: FeatureName;
  /** Optional — when set, the tab is hidden from `<RouterTabs>` and direct-URL
   *  access is blocked unless the caller's permission set grants this key
   *  (admins hold everything). The RBAC mirror of `AppRoute.requiredPermission`
   *  for detail-page sub-tabs. UX only — the `/api/*` handler each tab calls is
   *  the source of truth. The parent detail page filters the bar; a `<TabGuard>`
   *  in the tab element (or the parent's route registration) blocks deep links. */
  requiredPermission?: string;
  /** Optional — when `true`, the tab is hidden from supplier-tenant users
   *  (`session.is_supplier`). For internal-only surfaces inside a shared
   *  workspace (e.g. the Maxwell Reports tab). Server stays the source of
   *  truth (its endpoint 403/404s suppliers). */
  requiresInternal?: boolean;
  /** When true, the tab owns its own sub-routes — the route is registered
   *  with a wildcard suffix (`<path>/*`) so URLs like
   *  `/clients/:id/<path>/<sub-id>` still match this tab. The component
   *  is responsible for parsing the trailing segments via `useParams` /
   *  `useLocation`. The tab trigger keeps the `<NavLink end>` flag off so
   *  it stays highlighted when on a sub-route. */
  nested?: boolean;
  /** Nav-only counterpart of `nested`: the tab's sidebar row (in the PowerUp
   *  scope section) stays highlighted on deeper URLs that are separate routes,
   *  not this tab's own sub-routes — e.g. the RFQs tab while on the RFQ
   *  walkthrough. Changes no routing. See `AppRoute.activeOnSubpaths`. */
  activeOnSubpaths?: boolean;
  /** Nav-only: absolute patterns on which the tab's sidebar row also counts
   *  as active. See `AppRoute.alsoActiveOn`. */
  alsoActiveOn?: string[];
  /** Optional group label. When two adjacent tabs in the array have a
   *  different, both-defined `group`, `<RouterTabs>` renders a visual
   *  divider before the later tab's trigger (and a separator in the mobile
   *  select fallback). Tabs that omit `group` render with no divider — the
   *  default flat behavior every other `RouterTabs` consumer keeps. Used to
   *  distinguish "core entity" tabs from "this entity's activity in a
   *  specific app" tabs (e.g. the customer detail page's Client vs Sales
   *  activity groups) without splitting into two pages. */
  group?: string;
}

/**
 * Every top-level destination in the module lives here. The sidebar reads this
 * registry to build its nav sections; the router reads it to build its
 * <Route>s with `handle.meta` attached so breadcrumbs + document.title
 * resolve automatically.
 *
 * When you add a new page, add a single entry here. Mark it `status: "stub"`
 * to show it in the sidebar as a disabled placeholder (for pages not yet
 * ported), or `"live"` once its `element` is implemented.
 */
export interface AppRoute {
  /** URL path relative to the SPA basename (`/app`). */
  path: string;
  /** Sidebar section key this route belongs in. Omit to hide from the
   *  sidebar. A plain `string` so each module declares its own keys in its
   *  definition (`lib/modules/*`) without threading a union through this
   *  module; `app-registry` stamps it when composing a section, and
   *  `registerSectionLabels` supplies the header text. Keys must be globally
   *  unique across modules — see `registerSectionLabels`. */
  section?: string;
  /** Sidebar label. Also used as the default page title / breadcrumb leaf. */
  label: string;
  /** Sidebar icon. Required when `section` is set. */
  icon?: LucideIcon;
  /**
   * A small marker rendered AFTER the row's label.
   *
   * For a property of the row that is not its identity — Market Intelligence
   * marks a pinned pricing point this way. It is deliberately not `icon`:
   * that slot leads the row and reads as "what kind of thing is this", which
   * a pin is not. Trailing, it reads as a note about a row whose identity the
   * label already gave.
   */
  trailingIcon?: LucideIcon;
  /**
   * "live" → rendered by React Router; "stub" → sidebar-only placeholder;
   * "heading" → a label dividing the rows beneath it, never a destination.
   *
   * A `heading` row registers no route and is not clickable. It exists for a
   * nav list whose entries fall into groups the reader needs named — Market
   * Intelligence separates a region's forward-quoted hubs from its
   * settlement-only points this way, matching how the page groups them.
   */
  status: "live" | "stub" | "heading";
  /** React element rendered when status === "live". */
  element?: ReactElement;
  /**
   * Open this row in a new tab, and register no route for it.
   *
   * For a sidebar row that points at a page another module owns. Market
   * Intelligence lists its capacity reference data this way: the editor is
   * the super-admin console at `/settings/pjm-capacity`, and a second editor
   * over one dataset is what produced the drift this ticket removed. A new
   * tab because the reader is mid-analysis — sending them to Admin and
   * dropping their place in the module is a worse trade than a tab.
   *
   * Distinct from `status: "stub"`, which is a row with nowhere to go at all.
   * A `crossLink` row HAS a destination; it simply is not this module's.
   */
  crossLink?: boolean;
  /**
   * Keep this sidebar row highlighted on deeper URLs under its path that are
   * separate routes rather than registered `children` — e.g. the client's RFQs
   * row while on the RFQ walkthrough at `…/rfqs/:rfqId`, a chromeless
   * top-level route of its own. Nav-only: it changes `NavLeaf`'s match from
   * exact to prefix and nothing about routing. Rows match exactly by default
   * so a module-root row doesn't light up on every page beneath it (see
   * `NavLeaf`); opt in only where the deeper pages really are "inside" this
   * row.
   */
  activeOnSubpaths?: boolean;
  /**
   * Absolute route patterns (react-router syntax, prefix-matched) on which
   * this sidebar row also counts as active — for a page that lives at another
   * record's url but is reached *through* this row. E.g. the client's Deals
   * row on `/deals/:dealId`: a deal opened from the client's Deals tab is a
   * global record page, and the row went dark on it. Nav-only; no routing.
   *
   * Only safe where something keeps the row's own scope consistent with the
   * page — for the client rows, `DealDetailPage` adopts the deal's client into
   * the advisor picker, so the lit row is always that deal's client's.
   */
  alsoActiveOn?: string[];
  /**
   * Nav-only: exact pathnames on which this row, when it has children, starts
   * EXPANDED without counting as active. Unlike `alsoActiveOn`, nothing is lit
   * or filled: the row just opens. Market Intelligence uses it so a region's
   * All view opens both its Hubs and Load zones groups, since it shows both.
   *
   * On a `recordScope` section's rows the values are relative and resolve
   * against the scoped record, like `path`: `""` is the record's own url.
   */
  openOn?: string[];
  /**
   * Nav-only: on a row with children, print how many after its label while it
   * is collapsed, the way the region page's filter tabs print "Hubs 16".
   * Market Intelligence sets it on a region's Hubs, Load zones and Delivery
   * points rows.
   */
  showCount?: boolean;
  /** Optional breadcrumb trail override. When omitted, the trail defaults to
   *  a single crumb with this route's label. Provide an explicit trail for
   *  nested pages (e.g. a customer detail page under Customers). */
  breadcrumbs?: Breadcrumb[];
  /** Optional title override. Defaults to `label`. */
  title?: string;
  /**
   * Optional super-admin gate. When `true`, the route is hidden from the
   * sidebar and direct-URL access is blocked unless the caller is a
   * platform super-admin (an admin of a Pilot-internal tenant, in whichever
   * tenant they are signed in to — exposed on the session as
   * `is_super_admin`).
   *
   * This is UX. The server-side `/api/admin/*` endpoints re-check the
   * predicate via `require_super_admin` — the SPA gate just keeps the
   * link out of the sidebar and direct hits from rendering a flash of
   * page content before the empty/forbidden response lands.
   */
  requiresSuperAdmin?: boolean;
  /**
   * Optional supplier-portal gate. When `true`, the route is visible/reachable
   * only to supplier-tenant users (`session.is_supplier`). Used for the supplier
   * RFQ portal. Server-side supplier endpoints 404 non-suppliers — this is the
   * UX mirror.
   */
  requiredSupplier?: boolean;
  /**
   * When set, the sidebar hides this entry and `<ProtectedRoute>` blocks
   * direct-URL access unless the named per-tenant feature flag is enabled.
   * Server-side handlers remain the source of truth — this is UX, not
   * security. Matches the existing `requiredFeature` field on `TabRoute`.
   */
  requiredFeature?: FeatureName;
  /**
   * When set, the sidebar hides this entry and `<ProtectedRoute>` blocks
   * direct-URL access unless the caller's resolved permission set grants this
   * permission key (e.g. `"users.view"`). This is the RBAC mirror of the
   * server's `require_permission` gate — UX only; the `/api/*` handler is the
   * source of truth. Admins hold every permission, so admin-only surfaces stay
   * visible to them. This is the sole role-based gate — access is permission-
   * driven end to end (there is no persona/role concept anymore).
   */
  requiredPermission?: string;
  /**
   * Public routes render outside `<AppShell>` and skip `<ProtectedRoute>` —
   * they don't call `useSession()` and are usable while signed out. Used for
   * the SPA login + tenant-select pages. Always omit `section` so they don't
   * appear in the sidebar.
   */
  public?: boolean;
  /**
   * When true, AppShell renders this route as a sibling of `<Topbar>`
   * instead of nesting it inside the padded `<main>` element. The page
   * owns its own flex-column layout (header band, scrolling content
   * area, footer) — same structural pattern as `<Topbar>` itself.
   *
   * Use for pages that need a docked footer or full-canvas treatment
   * (e.g. the RFQ walkthrough's step footer). Page content is responsible
   * for its own padding; AppShell contributes only the bg + the flex
   * container that fills the right column.
   */
  chromeless?: boolean;
  /**
   * When true, AppShell renders this route with NO chrome at all — no
   * Sidebar, no Topbar, full viewport. Distinct from `chromeless`, which
   * only skips `<main>`'s padding but keeps Sidebar/Topbar rendered.
   * Use for standalone full-screen surfaces like the module launcher.
   */
  bare?: boolean;
  /**
   * Which module's chrome wraps this route — the sidebar `AppShell` renders.
   * Stamped automatically by `app-registry.tsx` from the module that declares
   * the route; you don't set it by hand in a module definition.
   *
   * Replaced the old `globalShell` / `reportsShell` boolean pair. One
   * boolean per shell didn't compose: every new module needed another field,
   * another `matches.some()` extractor in `AppShell`, and another branch in
   * its ternary cascade. A single key turns that cascade into a registry
   * lookup.
   *
   * **Shared record routes deliberately omit this.** A client belongs to no
   * single app (see `lib/nav-records.tsx`), so its detail page renders in
   * whichever module the user is currently in — resolved from nav context
   * rather than from the route. That's what stops a drill-in from being an
   * involuntary app switch, which is exactly what the old `globalShell` flag
   * on `/modules/global/clients/:id` caused.
   */
  moduleKey?: string;
  /**
   * Child tab routes. When set, the router nests each under this route's
   * path (e.g. `children: [{ path: "bills" }]` on `/clients/:id` creates
   * `/clients/:id/bills`). Visiting the parent path alone redirects to
   * the first non-disabled child.
   *
   * The parent `element` must render `<RouterTabs routes={children} />`
   * and `<Outlet />` — or use the combined `RouterTabs` API that handles
   * both. Pass per-tab data to children via `<Outlet context={...} />` and
   * read it with `useTabContext<T>()` in each child.
   */
  children?: TabRoute[];
  /**
   * Sidebar-only child entries. When set, this route renders in the
   * sidebar as an expandable group containing each `subItem` as an
   * indented child link. The sidebar collapse/expand toggle is
   * client-state only — it does NOT navigate to this route on click
   * (clicking the parent only expands/collapses).
   *
   * The parent itself usually has no route — it's a pure grouping node
   * (omit `element` and the router skips it). Children must each
   * declare their own full `path` (e.g. `/settings/feature-flags`)
   * and a normal `element`; they're registered with the router as
   * standalone routes.
   *
   * See "Sidebar nesting (parent groups)" in `src/web/CLAUDE.md` for
   * the full pattern.
   */
  subItems?: AppRoute[];
}

export const SECTION_LABELS: Record<NavSection, string> = {
  workspace: "Workspace",
  "ops-workspace": "Operations",
  "energy-advisor-settings": "Settings",
  "operations-settings": "Settings",
};

// Global's and Reports's own label maps used to sit here, alongside Sales's,
// purely to dodge a `routes.tsx` ⇄ registry import cycle. Each module now
// declares its section labels in its own definition (`lib/modules/*`) and pushes
// them in via `registerSectionLabels`, which resolves the cycle properly
// instead of centralizing every module's labels in this module.

export const APP_ROUTES: AppRoute[] = [
  // ── Public (no shell, no session dep) ────────────────────────────────────
  {
    path: "/login",
    label: "Sign in",
    status: "live",
    element: <LoginPage />,
    public: true,
    title: "Sign in",
  },
  {
    // Guest magic-link surface: the token in the URL is the credential,
    // scoped to one (RFQ, supplier). No session, no shell.
    path: "/portal/rfqs/:token",
    label: "Pricing request",
    status: "live",
    element: <PortalRfqPage />,
    public: true,
    title: "Pricing request",
  },
  {
    // A commission agent's deal update link: the token in the URL is the
    // credential, scoped to the agent's own deals. No session, no shell.
    path: "/portal/agent-deals/:token",
    label: "Your deals",
    status: "live",
    element: <AgentDealsPage />,
    public: true,
    title: "Your deals",
  },
  {
    path: "/tenant-select",
    label: "Choose tenant",
    status: "live",
    element: <TenantSelectPage />,
    public: true,
    title: "Choose tenant",
  },
  {
    path: "/access-denied",
    label: "Access denied",
    status: "live",
    element: <AccessDeniedPage />,
    public: true,
    title: "Access denied",
  },
  {
    // The design canvas — every primitive, composite, and recipe rendered
    // across every theme combination. Public (no shell, no session). The
    // page itself renders a 404 fallback when the host is Production.
    path: "/design",
    label: "Design",
    status: "live",
    element: <DesignPage />,
    public: true,
    title: "Design canvas",
  },
  {
    // Post-login landing — dashboard-first now (see `LauncherPage.tsx`'s
    // doc comment; this used to be a tile-grid-only page at `/modules`, and
    // the per-persona dashboard used to be its own separate Sales route at
    // this same `/dashboard` path — both merged here). No `section`: it's
    // not a sidebar destination. `bare` because it renders full-screen with
    // no sidebar/topbar at all.
    path: "/dashboard",
    label: "Home",
    status: "live",
    element: <LauncherPage />,
    title: "Home",
    breadcrumbs: [{ label: "Home" }],
    bare: true,
  },
  // ── Workspace ────────────────────────────────────────────────────────────
  {
    // The Maxwell workspace — Chat · Activity · Inbox. Reached via the
    // persistent Maxwell icon in `Topbar` (every shell — Sales, Global,
    // Reports — not just a Sales sidebar entry), plus the topbar bell
    // (→ Inbox) and the rail's pop-out. No `section`: intentionally hidden
    // from the Sales sidebar now that Maxwell has a shell-wide entry point,
    // ahead of Maxwell itself becoming a genuinely cross-module feature.
    // Visiting `/maxwell` redirects to the first tab (Chat).
    path: "/maxwell",
    label: "Maxwell",
    icon: Sparkles,
    status: "live",
    element: <MaxwellWorkspacePage />,
    title: "Maxwell",
    breadcrumbs: [{ label: "Maxwell" }],
    requiredFeature: "maxwell",
    requiredPermission: "maxwell.use",
    children: [...MAXWELL_TAB_ROUTES],
    // Full-height workspace: the chat transcript scrolls internally (composer
    // pinned) instead of the whole page growing. Every tab fills the bounded
    // column and owns its own scroll (see `maxwell-tabs.tsx`).
    chromeless: true,
  },
  {
    // Back-compat redirect: Watches lives in the Maxwell workspace at
    // `/maxwell/watches`. Kept ungated so existing links resolve regardless
    // of flags; the target route enforces its own gating.
    path: "/watches",
    label: "Watches",
    status: "live",
    element: <Navigate to="/maxwell/watches" replace />,
  },
  {
    // Back-compat redirect: the old standalone inbox now lives at
    // `/maxwell/inbox`. Kept ungated so existing links resolve regardless of
    // flags; the target route enforces its own gating.
    path: "/notifications",
    label: "Inbox",
    status: "live",
    element: <Navigate to="/maxwell/inbox" replace />,
  },
  // Deals (`/deals` + `/deals/:id`) is a shared record now, declared in
  // `lib/nav-records.tsx` and surfaced in the horizontal `RecordBar` rather
  // than Sales' Workspace section. Its routes register canonically via
  // `recordRoutes()`, so they must NOT also be declared here — a second
  // declaration is dead config the router dedupes away (see `shellRoutes`).
  //
  // It moved because deals aren't Sales-owned: Energy Insights and Market
  // Intelligence read them too, and a record in one module's sidebar reads as
  // belonging to that module.
  {
    // The supplier portal's solicitation list — supplier-persona users only;
    // the server answers 404 to anyone else.
    path: "/supplier/rfqs",
    section: "workspace",
    label: "RFQs",
    icon: Mail,
    status: "live",
    element: <SupplierRfqsPage />,
    title: "RFQs",
    breadcrumbs: [{ label: "RFQs" }],
    requiredSupplier: true,
  },
  {
    // Dynamic detail page. No section/nav registration — accessed via
    // drilldown from the supplier RFQ list.
    path: "/supplier/rfqs/:id",
    label: "Bid request",
    status: "live",
    element: <SupplierRfqDetail />,
    breadcrumbs: [{ label: "RFQs", href: "/supplier/rfqs" }, { label: "…" }],
    requiredSupplier: true,
  },
  {
    path: "/energy-advisor",
    // **No `section` — deliberately absent from the nav.** The client
    // is the module's *scope*, chosen in the sidebar-header picker
    // (`AdvisorClientPicker`), not a destination you travel to. A "Clients"
    // nav row here meant changing scope required visiting a page, and that
    // page then sat in the nav as a peer of the client's own tools.
    //
    // The route stays registered because it's the empty state — where
    // `entryPath` sends you when no client is in scope yet, and where the
    // customer page's 404 redirect lands.
    label: "Clients",
    icon: Briefcase,
    status: "live",
    element: (
      <EnergyAdvisorRouteWrapper>
        <EnergyAdvisorEntryRoute />
      </EnergyAdvisorRouteWrapper>
    ),
    requiredPermission: "energy_advisor.view",
  },
  {
    // Hidden drilldown route — reached only from the Energy Advisor landing. The
    // `EnergyAdvisorRouteWrapper` keeps the `EnergyAdvisorPresentationProvider` in scope
    // for every Energy Advisor subpath so the present-mode toggle persists across
    // navigation. `children` declares the tab structure; the router
    // auto-redirects `/energy-advisor/:customerId` to the first non-disabled
    // child (Snapshot).
    path: "/energy-advisor/:customerId",
    label: "Client",
    status: "live",
    element: (
      <EnergyAdvisorRouteWrapper>
        <EnergyAdvisorCustomerPage />
      </EnergyAdvisorRouteWrapper>
    ),
    // Static placeholder trail; `EnergyAdvisorCustomerPage` fills in the client
    // name at runtime via `usePageMeta` once it loads. Same breadcrumb shape
    // as every other detail page (AB#7743).
    breadcrumbs: [
      { label: "Energy Advisor", href: "/energy-advisor" },
      { label: "…" },
    ],
    requiredPermission: "energy_advisor.view",
    // Chromeless like the RFQ walkthrough below: the page re-applies the
    // shell's horizontal insets itself so the header band sits flush with
    // the topbar — identical top spacing to the walkthrough in both the
    // standard shell and present mode.
    chromeless: true,
    children: [...ENERGY_ADVISOR_CUSTOMER_TAB_ROUTES],
  },
  {
    // Full-canvas takeover for the RFQ walkthrough. Sibling of the
    // customer-detail tab shell (not a child) — the walkthrough is its
    // own surface with no sidebar and no tabs, so it bypasses
    // `EnergyAdvisorCustomerPage` and renders directly inside the Energy Advisor
    // presentation provider.
    path: "/energy-advisor/:customerId/rfqs/:rfqId",
    label: "RFQ walkthrough",
    status: "live",
    element: (
      <EnergyAdvisorRouteWrapper>
        <EnergyAdvisorRfqWalkthroughPage />
      </EnergyAdvisorRouteWrapper>
    ),
    breadcrumbs: [
      { label: "Energy Advisor", href: "/energy-advisor" },
      { label: "…" },
      { label: "RFQs" },
      { label: "…" },
    ],
    requiredPermission: "energy_advisor.view",
    chromeless: true,
  },

  // ── Operations ──────────────────────────────────────────────────────────
  {
    path: "/matrix-pricing",
    section: "ops-workspace",
    label: "Matrix Pricing",
    icon: Table2,
    status: "live",
    element: <MatrixPricingPage />,
    requiredPermission: "matrix_pricing.view",
  },
  {
    // Linked from the finder's status strip — never a sidebar entry.
    path: "/matrix-pricing/health",
    label: "Capture Health",
    title: "Capture Health",
    status: "live",
    element: <CaptureHealthPage />,
    requiredPermission: "matrix_pricing.view",
    breadcrumbs: [
      { label: "Matrix Pricing", href: "/matrix-pricing" },
      { label: "Capture Health" },
    ],
  },
  {
    // Drilled into from an exception or a coverage row — never a sidebar entry.
    path: "/matrix-pricing/:supplierId/:commodity",
    label: "Capture",
    status: "live",
    element: <SupplierCapturePage />,
    requiredPermission: "matrix_pricing.view",
  },
  {
    path: "/matrix-pricing/:supplierId/:commodity/mapping",
    label: "Mapping",
    status: "live",
    element: <MatrixMappingPage />,
    requiredPermission: "matrix_pricing.view",
  },
  {
    // AB#8451. Files under the Operations module's Pricing group (see
    // `OPERATIONS_GROUPS`), sorted A-Z there. Three tabs, each its own
    // nested route.
    path: "/dr-peak-shaving",
    section: "ops-workspace",
    label: "DR & Peak Shaving",
    icon: Activity,
    status: "live",
    element: <DrPeakShavingPage />,
    children: [...DR_PEAK_SHAVING_TAB_ROUTES],
    requiredPermission: "dr_peak_shaving.view",
  },
  {
    // AB#8345 — three calculator tabs, each its own nested route so the back
    // button traverses them and a link to one is shareable.
    path: "/ses-calculators",
    section: "ops-workspace",
    label: "SES Calculators",
    icon: Calculator,
    status: "live",
    element: <SesCalculatorsPage />,
    children: [...SES_CALCULATOR_TAB_ROUTES],
    requiredPermission: "ses_calculators.use",
  },
  {
    path: "/payees",
    section: "ops-workspace",
    label: "Payees",
    icon: Wallet,
    status: "live",
    element: <PayeesPage />,
    requiredPermission: "payees.view",
  },
  {
    // Not a sidebar entry — reached via the "Supplier Replies" button on the
    // Suppliers list page. Stays a registered route so the URL works directly.
    //
    // It briefly gained `section: "ops-workspace"` during the Operations
    // split, on the theory that a router-only route made inbound supplier
    // mail undiscoverable. That was wrong: the Suppliers page's own button is
    // the intended entry point, and a nav row alongside Suppliers duplicated
    // it. Keep it router-only.
    path: "/supplier-replies",
    label: "Supplier Replies",
    icon: Mail,
    status: "live",
    element: <SupplierRepliesPage />,
    requiredPermission: "suppliers.view",
  },
  {
    path: "/tasks",
    section: "ops-workspace",
    label: "Tasks",
    icon: CheckSquare,
    status: "live",
    element: <TasksPage />,
    requiredPermission: "tasks.view",
  },
  {
    // AB#8433 placed Cases below Tasks in the flat Operations list. The
    // PowerUp Operations module now groups its pages (Workflow: Cases, Tasks)
    // and sorts each group A-Z, so declaration order here no longer drives
    // the nav. See `OPERATIONS_GROUPS` in `lib/modules/operations-module.tsx`.
    path: "/cases",
    section: "ops-workspace",
    label: "Cases",
    icon: LifeBuoy,
    status: "live",
    element: <CasesPage />,
    requiredPermission: "cases.view",
  },

  // ── Settings ────────────────────────────────────────────────────────────
  //
  // Settings is a pure grouping node — no route, no element. Clicking the
  // sidebar entry only expands/collapses its `subItems`. See "Sidebar
  // navigation" in `pilot-web/CLAUDE.md`. Children kept alphabetical by label.
  //
  // There are TWO such groups below, one per module, because the config split
  // follows the pages it configures. Platform/tenant configuration (Users,
  // Segments, Permission Sets, Feature Flags, Reference Data, Product
  // Catalog) lives in the Admin module (`lib/modules/admin-module.tsx`).
  //
  // Both groups keep their `/settings/*` urls. The url is not the grouping
  // axis — re-pathing them to `/operations/settings/*` would break every
  // stored link and bookmark for a cosmetic gain, the same trade recorded in
  // `lib/record-paths.ts`. Sidebar placement is a nav decision; the url is a
  // contract.
  // Capability Proposals used to sit in Energy Advisor's group and moved to
  // Admin › Catalogs — it's the cross-tenant capability backlog, not advisor
  // config. Energy Advisor's group below now holds Portal Links and Scoring
  // Model instead.
  {
    // Energy Advisor's own config. Portal Links governs the guest links an
    // advisor mints to share an RFQ with a supplier; Scoring Model tunes the
    // product-fit rubric that drives the advisor's recommendations. Both are
    // knobs on the advisor's own workflow, so they belong to this module
    // rather than to Operations, where they sat briefly. Matrix Adder
    // Defaults sets the house adder the advisor's Matrix Pricing tab applies,
    // so it is here too; Operations' group keeps it beside Pass-through Rates.
    //
    // Distinct `path` from Operations' group below — a parent group's `path`
    // is the expand/collapse storage key and its group id, not a url the
    // router materializes (see `AppRoute.subItems`). Two groups sharing
    // `/settings` would collide on one `localStorage` key and toggle
    // together.
    path: "/energy-advisor-settings",
    section: "energy-advisor-settings",
    label: "Settings",
    icon: Settings,
    status: "live",
    subItems: [
      {
        path: "/settings/matrix-adder-defaults",
        label: "Matrix Adder Defaults",
        icon: BadgePlus,
        status: "live",
        element: <MatrixAdderDefaultsPage />,
        requiresSuperAdmin: true,
      },
      {
        path: "/settings/portal-links",
        label: "Portal Links",
        icon: Link2,
        status: "live",
        element: <PortalLinksPage />,
        requiresSuperAdmin: true,
      },
      {
        path: "/settings/scoring-model",
        label: "Scoring Model",
        icon: SlidersHorizontal,
        status: "live",
        element: <ScoringModelPage />,
        requiresSuperAdmin: true,
      },
    ],
  },
  {
    // Operations' own config: the Matrix Pricing tuning — Matrix Adder
    // Defaults (house adders) and Pass-through Rates (the rate grid) — and
    // the Auto Renewal Parameters that trigger the C&I renewal process.
    // Portal Links and Scoring Model moved to Energy Advisor; Pricing moved
    // to Admin (it's a tenant-wide display unit, not module config).
    path: "/operations-settings",
    section: "operations-settings",
    label: "Settings",
    icon: Settings,
    status: "live",
    subItems: [
      {
        path: "/settings/auto-renewal-parameters",
        label: "Auto Renewal Parameters",
        icon: Repeat,
        status: "live",
        element: <AutoRenewalParametersPage />,
        requiresSuperAdmin: true,
        requiredFeature: "auto_renewal",
      },
      {
        path: "/settings/matrix-adder-defaults",
        label: "Matrix Adder Defaults",
        icon: BadgePlus,
        status: "live",
        element: <MatrixAdderDefaultsPage />,
        requiresSuperAdmin: true,
      },
      {
        path: "/settings/passthrough-rates",
        label: "Pass-through Rates",
        icon: Gauge,
        status: "live",
        element: <PassthroughRatesPage />,
        requiresSuperAdmin: true,
      },
    ],
  },
];

/**
 * Returns `true` when the caller may visit `route`.
 *
 * Authorization is permission-based: a route with `requiredPermission` is
 * visible only when the caller's resolved permission set grants that key (or the
 * caller is an admin, who holds everything). `requiresSuperAdmin` and
 * `requiredFeature` layer on top. A route with none of these gates is open to
 * any authenticated caller — signalled by a non-`undefined` `permissions`.
 *
 * `permissions === undefined` (session not yet loaded) returns `false` for every
 * gated route so the sidebar stays empty until the session resolves rather than
 * flashing links.
 */
export function isRouteAuthorized(
  route: AppRoute,
  isSuperAdmin: boolean = false,
  features?: SessionFeatureFlags,
  permissions?: { granted: string[]; is_admin: boolean },
  isSupplier: boolean = false,
): boolean {
  if (route.requiresSuperAdmin && !isSuperAdmin) return false;
  if (route.requiredSupplier && !isSupplier) return false;
  if (route.requiredFeature && !(features?.[route.requiredFeature] ?? false)) {
    return false;
  }
  // RBAC permission gate: admins (sentinel) hold everything; otherwise the
  // resolved permission set must grant the key. Absent permissions → treat as
  // not-granted (session not yet loaded).
  if (route.requiredPermission) {
    if (!permissions) return false;
    if (!permissions.is_admin && !permissions.granted.includes(route.requiredPermission)) {
      return false;
    }
  }
  // No permission/feature/super-admin gate → open to any authenticated caller.
  // Gate on `permissions` presence so the sidebar stays empty pre-session.
  return permissions !== undefined;
}

/**
 * Whether the caller may see a detail-page sub-tab. Mirror of
 * `isRouteAuthorized` for `TabRoute`: a tab with `requiredFeature` needs the
 * flag on; a tab with `requiredPermission` needs the grant (admins hold
 * everything). Tabs with neither are open to any authenticated caller. Used both
 * to filter the `<RouterTabs>` bar and by `<TabGuard>` to block deep links.
 *
 * `permissions === undefined` (session not loaded) → hide, same as the sidebar.
 */
export function isTabAuthorized(
  tab: TabRoute,
  features?: SessionFeatureFlags,
  permissions?: { granted: string[]; is_admin: boolean },
  isSupplier: boolean = false,
): boolean {
  if (tab.requiresInternal && isSupplier) return false;
  if (tab.requiredFeature && !(features?.[tab.requiredFeature] ?? false)) {
    return false;
  }
  if (tab.requiredPermission) {
    if (!permissions) return false;
    if (
      !permissions.is_admin &&
      !permissions.granted.includes(tab.requiredPermission)
    ) {
      return false;
    }
  }
  return permissions !== undefined;
}

/**
 * Meta derived from a registry entry — used as the route's `handle.meta`.
 *
 * Default breadcrumb trail: `[Section, Page]` when the section label differs
 * from the page label (e.g. "Admin › Segments"), or just `[Page]` when
 * they match (e.g. "Customers" alone, not "Customers › Customers"). Pages
 * can override the whole trail by setting `breadcrumbs` explicitly on their
 * registry entry — useful for detail pages that want to include a parent
 * link (e.g. `[Customers, Customer name]`).
 */
/**
 * Section key → header label, across every module.
 *
 * `routeMetaFor` needs a label for whatever `section` a route carries,
 * without knowing which module declared it. Sourced from the module registry via a
 * lazy setter rather than a static merge of per-registry label maps: the
 * registry imports `AppRoute` from this module, so importing it back here
 * would close a cycle. `app-registry` calls `registerSectionLabels` at module
 * init instead.
 *
 * Section keys must stay globally unique across modules — two modules using one key
 * with different labels would resolve breadcrumbs inconsistently depending on
 * import order. Prefix per module when in doubt (`sales-records`, `mi-records`).
 */
const sectionLabels = new Map<string, string>(Object.entries(SECTION_LABELS));

export function registerSectionLabels(entries: Array<[string, string]>): void {
  for (const [key, label] of entries) sectionLabels.set(key, label);
}

export function routeMetaFor(route: AppRoute): PageMeta {
  let breadcrumbs: Breadcrumb[];
  if (route.breadcrumbs) {
    breadcrumbs = route.breadcrumbs;
  } else {
    const trail: Breadcrumb[] = [];
    if (route.section) {
      const sectionLabel = sectionLabels.get(route.section);
      // Skip the section crumb when the section has no label (an unlabeled
      // leading group) or when it would just repeat the page name — "Clients ›
      // Clients" reads worse than "Clients" alone.
      if (sectionLabel !== undefined && sectionLabel !== route.label) {
        // Section heading — not clickable (there is no section-level page).
        trail.push({ label: sectionLabel });
      }
    }
    trail.push({ label: route.label });
    breadcrumbs = trail;
  }

  return {
    title: route.title ?? route.label,
    breadcrumbs,
  };
}
